import Phaser from 'phaser';
import type { Hero, Equipment, EquipmentSlot } from '@roe/shared';
import { setupCamera, GAME_H } from '../dpr.js';
import { createHudTooltip, createItemTooltip } from '../utils/tooltip';
import { buildEquipStatusAccent, formatEquipmentTooltipLines, getArmorTypeLabel, getWeaponTypeLabel } from '../utils/heroEquip';
import { PhaserTextInput } from '../utils/PhaserTextInput';

/** Format a stat bonus value with at most 2 decimal places — prevents float noise like 1.5000000000000002 */
function fmtStatVal(v: number): string {
  if (v < 1) return `+${(Math.round(v * 1000) / 10).toFixed(1)}%`;
  return `+${Math.round(v * 100) / 100}`;
}

/** Pretty labels for stat keys — matches InventoryScene's STAT_LABELS so tooltips read identically
 *  across both panels. Resist entries use emoji + "Res" for the compact 3-stat rows under equipped items. */
const STAT_LABEL: Record<string, string> = {
  hp: 'HP', mp: 'Mana', attack: 'ATK', defense: 'DEF', speed: 'SPD',
  critChance: 'Crit', critDamage: 'CritDmg', spellPower: 'SP',
  fireResist: '🔥 Res', frostResist: '❄️ Res', shadowResist: '🌑 Res', natureResist: '🌿 Res',
  atkPct: 'ATK', spellPowerPct: 'SP', critDmgPct: 'CritDmg', attackSpeedPct: 'Attack Speed',
  defPct: 'DEF', hpPct: 'HP', maxMpPct: 'Max MP', mpRegenPct: 'MP Regen',
  goldPct: 'Gold', lootPct: 'Loot', xpPct: 'XP', ignoreArmorPct: 'Armor Pen',
  blockPct: 'Block', cdrPct: 'Cooldown',
  vsBeastPct: 'vs Beast', vsDemonPct: 'vs Demon', vsUndeadPct: 'vs Undead',
  vsOrcPct: 'vs Orc', vsDragonPct: 'vs Dragon', vsMinotaurPct: 'vs Minotaur',
  vsVoidPct: 'vs Void', vsBossPct: 'vs Boss',
};
function fmtStatLabel(k: string): string {
  if (STAT_LABEL[k]) return STAT_LABEL[k];
  if (k.endsWith('Pct')) {
    const r = k.slice(0, -3);
    return r.charAt(0).toUpperCase() + r.slice(1);
  }
  return k;
}
function formatItemBonus(k: string, v: number | string): string {
  const val = k.endsWith('Resist')
    ? `+${Math.round(v as number)}%`
    : (typeof v === 'number' ? fmtStatVal(v) : `+${v}`);
  return `${val} ${fmtStatLabel(k)}`;
}
import { getEffectiveStats, RARITY_COLORS, HERO_CLASS_NAMES, getTomeBonus, WEAPON_CLASS_RESTRICTIONS, ARMOR_CLASS_RESTRICTIONS, getTalentBonusValue, getTalentUpgradeInfo, getNextRankLabel, getTalentTotalLabel, getRelicScaledEffect, getRelicPassiveBonuses, calculateMitigation, getPassiveBonuses, getSpentTalentPoints, MAX_TALENT_POINTS, TWO_HANDED_WEAPON_TYPES, formatAffix, aggregateBoonEffects } from '@roe/shared';
import { GameStateManager } from '../state/GameStateManager';
import { drawPadlock } from '../utils/glyphs';

export class HeroScene extends Phaser.Scene {
  private gsm!: GameStateManager;
  private battleScene!: Phaser.Scene & { resumeBattle(): void };
  private infernoMode = false;
  private selectedHeroIndex = 0;
  private contentGroup!: Phaser.GameObjects.Group;
  private inventoryScrollOffset = 0;
  private readonly ITEMS_PER_PAGE = 7;
  private inventoryFilter: 'all' | 'weapon' | 'armor' | 'relic' | 'trophy' | 'sigil' = 'all';
  private inventorySearch = '';
  private searchInput: PhaserTextInput | null = null;
  private equipTooltip: Phaser.GameObjects.Container | Phaser.GameObjects.Text | null = null;
  /** Shift-hold compare state + active tooltip rebuilder */
  private shiftHeld = false;
  // Builder rebuilds the whole tooltip in-place (destroys + recreates) so the
  // shift-compare listener can re-fire it when SHIFT is held/released.
  private currentTooltipBuilder: (() => void) | null = null;
  private currentTooltipPos: { x: number; y: number } = { x: 0, y: 0 };
  private resistTexts: Phaser.GameObjects.Text[] = [];
  /** Single-text resist line — refreshed every 300ms so in-battle Arcane Might etc. show live. */
  /** Reference to the combined resist row — four sub-text objects colour-coded
   *  per school so the player can see the numbers at a glance (fire red / frost
   *  blue / shadow purple / nature green). Kept as a single object reference so
   *  the 300 ms refresh timer can update all four without regrowing the list. */
  private resistLine: {
    fire: Phaser.GameObjects.Text;
    frost: Phaser.GameObjects.Text;
    shadow: Phaser.GameObjects.Text;
    nature: Phaser.GameObjects.Text;
  } | null = null;

  constructor() {
    super({ key: 'HeroScene' });
  }

  init(data: { gsm: GameStateManager; battleScene: any; infernoMode?: boolean }): void {
    this.gsm = data.gsm;
    this.battleScene = data.battleScene;
    this.infernoMode = data.infernoMode ?? false;
  }

  create(): void {
    setupCamera(this, this.cameras.main, 'overlay');
    // Reset state on each open (scene persists as overlay between sessions)
    this.selectedHeroIndex = 0;
    this.inventoryScrollOffset = 0;
    this.inventoryFilter = 'all';
    this.inventorySearch = '';
    this.shiftHeld = false;

    // Shift-hold listener for compare mode — rebuilds the active tooltip with diff stats.
    // Tooltips are now Containers (no setText), so we fully rebuild via the stored builder.
    const rebuildCompareTooltip = (): void => {
      if (!this.equipTooltip || !this.currentTooltipBuilder) return;
      // Builder destroys + recreates this.equipTooltip internally using the
      // current shiftHeld state, so just call it again.
      this.currentTooltipBuilder();
    };
    const shiftKey = this.input.keyboard?.addKey(Phaser.Input.Keyboard.KeyCodes.SHIFT);
    shiftKey?.on('down', () => {
      if (this.shiftHeld) return;
      this.shiftHeld = true;
      rebuildCompareTooltip();
    });
    shiftKey?.on('up', () => {
      this.shiftHeld = false;
      rebuildCompareTooltip();
    });

    // Phaser-native input cleans itself up on shutdown automatically — still restore
    // BattleScene + keyboard capture state regardless of how this scene was closed.
    this.events.once('shutdown', () => {
      this.searchInput = null;
      this.input.keyboard?.enableGlobalCapture();
      this.battleScene?.resumeBattle();
    });

    // Half-screen overlay on the right side
    this.cameras.main.setBackgroundColor('rgba(5, 3, 5, 0.97)');
    this.contentGroup = this.add.group();

    const W = 480;

    // ── Header ──
    this.add.rectangle(W / 2, 22, W, 44, 0x0a0806, 1).setDepth(10);
    this.add.rectangle(W / 2, 44, W, 1, 0x5a3600, 0.9).setDepth(10);
    this.add.text(W / 2, 20, '✦  Heroes  ✦', {
      fontSize: '16px', fontFamily: 'Georgia, serif', color: '#c8973a', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(11);

    // Close button
    const closeBtn = this.add.text(W - 20, 20, '✕', {
      fontSize: '16px', fontFamily: 'Georgia, serif', color: '#5a3600',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true }).setDepth(11);
    closeBtn.on('pointerover', () => closeBtn.setColor('#c8973a'));
    closeBtn.on('pointerout', () => closeBtn.setColor('#5a3600'));
    closeBtn.on('pointerdown', () => this.closeScene());

    // Direct nav to Inventory — forward-arrow glyph per user feedback ("INV"
    // wasn't readable as a nav action). Bronze on dim, brightens on hover;
    // hover tooltip says "Inventory →" so the destination stays explicit.
    const goInvBg = this.add.rectangle(W - 60, 20, 40, 22, 0x140e06, 0.9)
      .setStrokeStyle(1, 0x5a3600).setDepth(11).setInteractive({ useHandCursor: true });
    const goInvBtn = this.add.text(W - 60, 19, '→', {
      fontSize: '18px', fontFamily: 'Georgia, serif', color: '#c8973a', fontStyle: 'bold',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true }).setDepth(12);
    const onInvOver = () => { goInvBtn.setColor('#fbbf24'); goInvBg.setStrokeStyle(1, 0x9a6020); };
    const onInvOut  = () => { goInvBtn.setColor('#c8973a'); goInvBg.setStrokeStyle(1, 0x5a3600); };
    goInvBg.on('pointerover', onInvOver).on('pointerout', onInvOut);
    goInvBtn.on('pointerover', onInvOver).on('pointerout', onInvOut);
    const doGoInv = () => {
      this.searchInput?.destroy();
      this.searchInput = null;
      this.input.keyboard?.enableGlobalCapture();
      this.gsm.save();
      this.scene.stop();
      this.scene.launch('InventoryScene', { gsm: this.gsm, battleScene: this.battleScene });
    };
    goInvBg.on('pointerdown', doGoInv);
    goInvBtn.on('pointerdown', doGoInv);
    let goInvTip: Phaser.GameObjects.Text | null = null;
    const showInvTip = () => {
      goInvTip?.destroy();
      goInvTip = this.add.text(W - 60, 38, 'Inventory →', {
        fontSize: '9px', fontFamily: 'Georgia, serif', color: '#c8973a',
        backgroundColor: '#0d0a0e', padding: { x: 4, y: 2 },
      }).setOrigin(0.5, 0).setDepth(20);
    };
    const hideInvTip = () => { goInvTip?.destroy(); goInvTip = null; };
    goInvBg.on('pointerover', showInvTip).on('pointerout', hideInvTip);
    goInvBtn.on('pointerover', showInvTip).on('pointerout', hideInvTip);

    // ESC closes the scene
    if (this.input.keyboard) {
      this.input.keyboard.once('keydown-ESC', () => this.closeScene());
    }

    // Inventory scroll buttons
    const scrollUp = this.add.text(W - 20, 460, '▲', { fontSize: '12px', fontFamily: 'Georgia, serif', color: '#5a3600' })
      .setOrigin(0.5).setInteractive({ useHandCursor: true });
    scrollUp.on('pointerover', () => scrollUp.setColor('#c8973a'));
    scrollUp.on('pointerout', () => scrollUp.setColor('#5a3600'));
    scrollUp.on('pointerdown', () => { this.inventoryScrollOffset = Math.max(0, this.inventoryScrollOffset - 1); this.refreshContent(); });
    const scrollDn = this.add.text(W - 20, 478, '▼', { fontSize: '12px', fontFamily: 'Georgia, serif', color: '#5a3600' })
      .setOrigin(0.5).setInteractive({ useHandCursor: true });
    scrollDn.on('pointerover', () => scrollDn.setColor('#c8973a'));
    scrollDn.on('pointerout', () => scrollDn.setColor('#5a3600'));
    scrollDn.on('pointerdown', () => { this.inventoryScrollOffset++; this.refreshContent(); });

    // Mouse wheel scroll for inventory
    this.input.on('wheel', (_ptr: unknown, _gos: unknown, _dx: unknown, dy: number) => {
      if (dy > 0) this.inventoryScrollOffset++;
      else this.inventoryScrollOffset = Math.max(0, this.inventoryScrollOffset - 1);
      this.refreshContent();
    });

    this.createSearchInput();
    this.refreshContent();

    // Refresh resist panel live so in-battle buffs (Arcane Might etc.) show current values.
    // Note: resistTexts is currently unused — the resist line is a single combined text
    // in the stat column now. Fall back to resistLine if it exists.
    this.time.addEvent({
      delay: 300,
      loop: true,
      callback: () => {
        const hero = this.gsm.player.heroes[this.selectedHeroIndex];
        if (!hero || !this.resistLine?.fire?.active) return;
        const s = getEffectiveStats(hero);
        // Sigil affix aggregation — fold elemental + all-resist contributions into the live line
        // so the number the player sees in the panel matches what combat applies.
        const sig = this.aggregateSigilAffixes();
        // Arcane Ward Stone (+5% all resist) is party-wide — include it so the live-tick doesn't
        // overwrite the initial render with a lower value while the trophy is equipped.
        const trophyAll = this.gsm.player.deployedHeroes.some((idx) => {
          const h = this.gsm.player.heroes[idx];
          return !!h?.equipment.reserved?.specialEffect?.includes('all resist');
        }) ? 5 : 0;
        const bEff = aggregateBoonEffects(this.gsm.player.activeBoons);
        // Elixir of Resistance adds _resistanceBuff to every alive hero in
        // combat (line ~18135 in BattleScene). Mirror it here so the panel
        // matches what getHeroResist applies during damage calc.
        const elixirResist = Math.max(0, (hero as any)._resistanceBuff || 0);
        const fire   = Math.min((s.fireResist   ?? 0) + trophyAll + sig.fireResist   + sig.allResist + bEff.fireResistBonus   + elixirResist, 75);
        const frost  = Math.min((s.frostResist  ?? 0) + trophyAll + sig.frostResist  + sig.allResist + bEff.frostResistBonus  + elixirResist, 75);
        const shadow = Math.min((s.shadowResist ?? 0) + trophyAll + sig.shadowResist + sig.allResist + bEff.shadowResistBonus + elixirResist, 75);
        const nature = Math.min((s.natureResist ?? 0) + trophyAll + sig.natureResist + sig.allResist + bEff.natureResistBonus + elixirResist, 75);
        this.resistLine.fire.setText(`${fire}%`);
        this.resistLine.frost.setText(`${frost}%`);
        this.resistLine.shadow.setText(`${shadow}%`);
        this.resistLine.nature.setText(`${nature}%`);
      },
    });
  }

  /** Sum sigil affixes across the player's equipped sigils — mirrors BattleScene.getSigilEffects()
   *  but HeroScene-local so we can show the contributions without a cross-scene call.
   *  Returns only the affix keys we render in the stats panel. */
  private aggregateSigilAffixes(): {
    atkPct: number; spellPowerPct: number; critChance: number; critDmgPct: number;
    attackSpeedPct: number; defPct: number; hpPct: number; maxMpPct: number;
    mpRegenPct: number;
    fireResist: number; frostResist: number; shadowResist: number; natureResist: number; allResist: number;
    stunResist: number; petrifyResist: number; fearResist: number;
    goldPct: number; lootPct: number; xpPct: number;
    ignoreArmorPct: number; vsBossPct: number;
  } {
    const agg = {
      atkPct: 0, spellPowerPct: 0, critChance: 0, critDmgPct: 0,
      attackSpeedPct: 0, defPct: 0, hpPct: 0, maxMpPct: 0, mpRegenPct: 0,
      fireResist: 0, frostResist: 0, shadowResist: 0, natureResist: 0, allResist: 0,
      stunResist: 0, petrifyResist: 0, fearResist: 0,
      goldPct: 0, lootPct: 0, xpPct: 0,
      ignoreArmorPct: 0, vsBossPct: 0,
    };
    const equipped = this.gsm.player.sigilsEquipped ?? [];
    const bag = this.gsm.player.sigilBag ?? [];
    const collection = this.gsm.account?.sigilCollection ?? [];
    for (const sigilId of equipped) {
      const sigil = bag.find((s) => s.id === sigilId) ?? collection.find((s) => s.id === sigilId);
      if (!sigil) continue;
      for (const a of sigil.affixes) {
        const k = a.key as keyof typeof agg;
        // Faction-specific affixes (vsBeastPct etc.) are not rendered in the compact panel
        // summary — they live in the per-sigil tooltip. Everything else in `agg` gets added.
        if (k in agg) (agg as Record<string, number>)[k] += a.value;
      }
    }
    return agg;
  }

  /** Phaser-native search input — replaces the old HTML overlay. Lives entirely inside
   *  the scene graph so there's no DOM sync bug (no position drift on resize, no stale
   *  element after scene transitions, no focus leaks). */
  private createSearchInput(): void {
    this.searchInput?.destroy();
    // Six filter buttons (All/Wpn/Arm/Rel/Tro/Sig) span x ≈ 25..245. Search input sits to
    // the right of them; top-left anchor so coordinates match the filter row's baseline.
    const SEARCH_X = 260;
    const SEARCH_W = 100;
    const SEARCH_H = 16;
    const SEARCH_Y = 424 - Math.round(SEARCH_H / 2);
    // Debounce the live filter rebuild — refreshContent recreates dozens of
    // sprites/text objects, so calling it on every keystroke makes fast typing
    // feel laggy. 60 ms is short enough to read as instant and long enough to
    // coalesce a typed word into a single rebuild.
    let pending: ReturnType<typeof setTimeout> | null = null;
    this.searchInput = new PhaserTextInput(this, {
      x: SEARCH_X, y: SEARCH_Y, width: SEARCH_W, height: SEARCH_H,
      placeholder: 'Search…',
      initialValue: this.inventorySearch,
      fontSize: '10px',
      depth: 25,
      onChange: (v) => {
        this.inventorySearch = v.toLowerCase();
        this.inventoryScrollOffset = 0;
        if (pending) clearTimeout(pending);
        pending = setTimeout(() => { pending = null; this.refreshContent(); }, 60);
      },
    });
  }

  private refreshContent(): void {
    // Destroy any active tooltip before clearing content group — it lives outside the group
    this.equipTooltip?.destroy();
    this.equipTooltip = null;
    this.contentGroup.clear(true, true);

    const W = 480;
    const heroes = this.gsm.player.heroes;
    const heroCount = Math.max(1, heroes.length);

    // ── Hero Tabs (compact for 480px) ──
    const maxTabArea = 360;
    const tabW = Math.min(90, Math.floor(maxTabArea / heroCount));

    heroes.forEach((hero, i) => {
      const isActive = i === this.selectedHeroIndex;
      const tabBg = this.add.rectangle(10 + i * tabW + tabW / 2, 51, tabW - 4, 20,
        isActive ? 0x1a0f06 : 0x0d0a08, 1)
        .setStrokeStyle(1, isActive ? 0xc8973a : 0x5a3600)
        .setInteractive({ useHandCursor: true });
      const tabTxt = this.add.text(10 + i * tabW + tabW / 2, 51,
        `${hero.name}  Lv${hero.level}`, {
          fontSize: '9px', fontFamily: 'Georgia, serif',
          color: isActive ? '#c8973a' : '#6b4020', align: 'center',
        }).setOrigin(0.5).setInteractive({ useHandCursor: true });
      this.contentGroup.add(tabBg);
      this.contentGroup.add(tabTxt);
      const doSwitch = () => { this.selectedHeroIndex = i; this.refreshContent(); };
      tabBg.on('pointerdown', doSwitch);
      tabTxt.on('pointerdown', doSwitch);
      tabBg.on('pointerover', () => { tabTxt.setColor('#c8973a'); tabBg.setStrokeStyle(1, 0xc8973a); });
      tabBg.on('pointerout', () => { tabTxt.setColor(isActive ? '#c8973a' : '#6b4020'); tabBg.setStrokeStyle(1, isActive ? 0xc8973a : 0x5a3600); });
    });

    // Slot display
    const deployed = this.gsm.player.deployedHeroes.length;
    const slots = this.gsm.getMaxDeploySlots();
    const aliveDeployed = this.gsm.player.deployedHeroes.filter((idx) => {
      const h = this.gsm.player.heroes[idx];
      return h && !h.isDead && !h.permanentlyDead;
    }).length;
    const slotLabel = this.add.text(W - 80, 46, `Slots ${aliveDeployed}/${slots}`, {
      fontSize: '9px', fontFamily: 'Georgia, serif', color: '#5a3600',
    });
    this.contentGroup.add(slotLabel);

    // ── Equipped sigil strip (bottom-right, left of the deploy button) ──
    this.renderSigilStrip();

    // ── Hero content ──
    const hero = heroes[this.selectedHeroIndex];
    if (!hero) return;

    const stats = getEffectiveStats(hero);
    const isDeployed = this.gsm.player.deployedHeroes.includes(this.selectedHeroIndex);
    // Mirror BattleScene.effStats so the Hero panel matches what combat actually
    // applies. Layers (in order): v2 talent tree (hpFlat / hpPctBonus / defFlat),
    // sigil affixes (hpPct / maxMpPct / defPct), Valka armory passive
    // (Warmaiden's Grit +15 % HP), then run-long boon modifiers.
    const pb = getPassiveBonuses(this.gsm.player.passiveTree ?? {});
    if (pb.hpFlat > 0)     stats.maxHp   = stats.maxHp + pb.hpFlat;
    if (pb.hpPctBonus > 0) stats.maxHp   = Math.floor(stats.maxHp * (1 + pb.hpPctBonus));
    if (pb.defFlat > 0)    stats.defense = stats.defense + pb.defFlat;
    {
      const equippedSigils = this.gsm.player.sigilsEquipped ?? [];
      const sigilBagAll = this.gsm.player.sigilBag ?? [];
      const sigilCollection = this.gsm.account.sigilCollection ?? [];
      let hpPctSum = 0, maxMpPctSum = 0, defPctSum = 0;
      for (const sid of equippedSigils) {
        const s = sigilBagAll.find((b) => b.id === sid) ?? sigilCollection.find((b) => b.id === sid);
        if (!s) continue;
        for (const a of s.affixes) {
          if (a.key === 'hpPct')    hpPctSum += a.value;
          if (a.key === 'maxMpPct') maxMpPctSum += a.value;
          if (a.key === 'defPct')   defPctSum += a.value;
        }
      }
      if (hpPctSum > 0)    stats.maxHp   = Math.floor(stats.maxHp   * (1 + hpPctSum / 100));
      if (maxMpPctSum > 0) stats.maxMp   = Math.floor(stats.maxMp   * (1 + maxMpPctSum / 100));
      if (defPctSum > 0)   stats.defense = Math.floor(stats.defense * (1 + defPctSum / 100));
    }
    if (hero.activePassiveIds?.includes('passive_valka_warmaidens_grit')) {
      stats.maxHp = Math.floor(stats.maxHp * 1.15);
    }
    // Shrine of Boons — run-long stat modifiers (positive blessings + negative curses).
    const boonEff = aggregateBoonEffects(this.gsm.player.activeBoons);
    if (boonEff.hasAny) {
      if (boonEff.maxHpPct       !== 0) stats.maxHp      = Math.max(1, Math.floor(stats.maxHp   * (1 + boonEff.maxHpPct)));
      if (boonEff.maxMpPct       !== 0) stats.maxMp      = Math.max(0, Math.floor(stats.maxMp   * (1 + boonEff.maxMpPct)));
      if (boonEff.defPct         !== 0) stats.defense    = Math.max(0, Math.floor(stats.defense * (1 + boonEff.defPct)));
      if (boonEff.critDamagePct  !== 0) stats.critDamage = Math.max(1.0, (stats.critDamage ?? 1) + boonEff.critDamagePct);
      if (boonEff.attackSpeedPct !== 0) stats.speed      = Math.max(0.1, stats.speed * (1 + boonEff.attackSpeedPct));
    }

    // ── MIDDLE-CENTER column: portrait → kills → XP bar ──
    const portraitX = 240;
    const xpBarW = 110;
    const xpPct = Math.min(1, hero.xp / hero.xpToNextLevel);

    const killsStr = hero.totalKills >= 1_000_000
      ? `${(hero.totalKills / 1_000_000).toFixed(1)}M`
      : hero.totalKills >= 10_000
        ? `${Math.floor(hero.totalKills / 1000)}k`
        : `${hero.totalKills}`;
    // Kills sits just below the portrait
    const killsLabel = this.add.text(portraitX, 348, `Kills  ${killsStr}`, {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#c8973a', fontStyle: 'bold', align: 'center',
    }).setOrigin(0.5, 0);
    // XP label + bar sit BELOW kills
    const xpLabel = this.add.text(portraitX, 368, `XP  ${hero.xp} / ${hero.xpToNextLevel}`, {
      fontSize: '10px', fontFamily: 'Georgia, serif', color: '#a07840', fontStyle: 'bold', align: 'center',
    }).setOrigin(0.5, 0);
    const xpBarBg  = this.add.rectangle(portraitX, 389, xpBarW, 6, 0x2a1a08).setOrigin(0.5, 0.5);
    const xpBarFill = this.add.rectangle(portraitX - xpBarW / 2, 389, Math.max(2, xpBarW * xpPct), 6, 0xc8973a).setOrigin(0, 0.5);
    this.contentGroup.add(xpLabel);
    this.contentGroup.add(xpBarBg);
    this.contentGroup.add(xpBarFill);
    this.contentGroup.add(killsLabel);

    // Portrait — sits in the middle vertically, below the XP bar and above the kills label.
    const portraitY = 278; // bottom-anchor: sprite bottom at 278+64=342, top at 342-144=198
    const heroSpriteKey = `hero_${hero.heroKey ?? hero.heroClass}`;
    const portrait = this.add.sprite(portraitX, portraitY + 64, heroSpriteKey)
      .setDisplaySize(144, 144).setOrigin(0.5, 1.0);
    const idleAnimKey = `${heroSpriteKey}_idle`;
    if (this.anims.exists(idleAnimKey)) portrait.play(idleAnimKey);
    this.contentGroup.add(portrait);

    // Resistance panel removed — resists now shown inline in the stats column below CritDmg
    this.resistTexts = [];

    // Deploy / status button — very bottom-right of the page.
    const btnX = 420;
    const btnY = 555;
    if (hero.permanentlyDead) {
      const reviveCost = this.gsm.getGoldReviveCost(this.selectedHeroIndex);
      const canAfford = this.gsm.player.resources.gold >= reviveCost;
      const reviveBtn = this.add.text(btnX, btnY, `REVIVE\n🪙${reviveCost}`, {
        fontSize: '10px', fontFamily: 'Georgia, serif', color: canAfford ? '#e8a030' : '#5a3600',
        backgroundColor: canAfford ? '#1a0608' : '#0d0a0e',
        padding: { x: 8, y: 3 }, align: 'center',
      }).setOrigin(0.5).setInteractive({ useHandCursor: canAfford });
      this.contentGroup.add(reviveBtn);
      if (canAfford) {
        reviveBtn.on('pointerdown', () => {
          if (this.gsm.goldRevive(this.selectedHeroIndex)) {
            if (!this.gsm.player.deployedHeroes.includes(this.selectedHeroIndex)) {
              this.gsm.deployHero(this.selectedHeroIndex);
            }
            this.refreshContent();
          }
        });
      }
    } else if (hero.isDead && (hero.goldReviveTimer ?? 0) > 0) {
      const revSec = ((hero.goldReviveTimer ?? 0) / 1000).toFixed(1);
      const revLabel = this.add.text(btnX, btnY, `⟳ Reviving ${revSec}s`, {
        fontSize: '10px', fontFamily: 'Georgia, serif', color: '#fbbf24',
        backgroundColor: '#1a0e00', padding: { x: 8, y: 3 },
      }).setOrigin(0.5);
      this.contentGroup.add(revLabel);
    } else if (hero.isDead) {
      const respawnSec = Math.ceil((hero.respawnTimer || 0) / 1000);
      const deadLabel = this.add.text(btnX, btnY, `DEAD  ${respawnSec}s`, {
        fontSize: '10px', fontFamily: 'Georgia, serif', color: '#c8150f',
        backgroundColor: '#0d0a0e', padding: { x: 10, y: 3 },
      }).setOrigin(0.5);
      this.contentGroup.add(deadLabel);
    } else if (this.infernoMode) {
      const label = this.add.text(btnX, btnY, isDeployed ? '— INFERNO —' : 'BENCH', {
        fontSize: '9px', fontFamily: 'Georgia, serif', color: '#5a1a00',
        backgroundColor: '#0d0402', padding: { x: 10, y: 4 },
      }).setOrigin(0.5);
      this.contentGroup.add(label);
    } else if (isDeployed && this.selectedHeroIndex === 0) {
      const mainLabel = this.add.text(btnX, btnY, 'MAIN HERO', {
        fontSize: '9px', fontFamily: 'Georgia, serif', color: '#7a5010',
        backgroundColor: '#0d0a08', padding: { x: 10, y: 4 },
      }).setOrigin(0.5);
      this.contentGroup.add(mainLabel);
    } else {
      const canDeploy = !isDeployed && aliveDeployed < slots;
      const deployBtn = this.add.text(btnX, btnY, isDeployed ? 'UNDEPLOY' : 'DEPLOY', {
        fontSize: '10px', fontFamily: 'Georgia, serif', color: isDeployed ? '#e8a030' : '#6aaa60', fontStyle: 'bold',
        backgroundColor: isDeployed ? '#1a0608' : '#0e1a0e',
        padding: { x: 10, y: 4 },
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });
      this.contentGroup.add(deployBtn);

      if (canDeploy) {
        this.tweens.add({ targets: deployBtn, alpha: 0.4, duration: 600, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
      }

      deployBtn.on('pointerdown', () => {
        if (isDeployed) {
          this.gsm.undeployHero(this.selectedHeroIndex);
        } else {
          const ok = this.gsm.deployHero(this.selectedHeroIndex);
          if (!ok) { this.showMessage('No hero slots available!'); return; }
        }
        this.refreshContent();
      });
    }

    // ── TOP-LEFT: stats with resists inline ──
    const statsX = 10;
    const statsY = 80; // bumped 60 → 80 per user feedback so the stat list breathes against the panel header
    const statGap = 17; // tightened with the 12px → 10px stat font shrink so saved row height reclaims space below
    const tomes = this.gsm.player.tomes;

    // Tome bonuses that affect hero stats
    const tDmg     = Math.floor(getTomeBonus(tomes, 'damage'));
    const tDef     = Math.floor(getTomeBonus(tomes, 'armor'));
    const tCrit    = getTomeBonus(tomes, 'crit');
    const tCritDmg = getTomeBonus(tomes, 'critDamage');
    // Book of the Archivist also contributes +2 SP per rank — include it here so the
    // hero panel total matches what's actually applied in combat.
    const tSp      = Math.floor(
      getTomeBonus(tomes, 'spellpower') + getTomeBonus(tomes, 'book_archivist'),
    );
    const tHp      = getTomeBonus(tomes, 'health');
    const tSpd     = getTomeBonus(tomes, 'speed');

    // Trophy bonuses that affect displayed stats (any deployed hero's trophy applies to all heroes)
    const hasTrophy = (substr: string): boolean => this.gsm.player.deployedHeroes.some((idx) => {
      const h = this.gsm.player.heroes[idx];
      return !!h?.equipment.reserved?.specialEffect?.includes(substr);
    });
    // Elixir / War Horn are equipped-hero-only — read this hero's own trophy
    const heroTrophyEffect = hero.equipment.reserved?.specialEffect ?? '';
    const trophyAtkPct     = heroTrophyEffect.includes('base attack damage') ? 0.25 : 0; // Elixir of Giants
    const trophyCritPct    = heroTrophyEffect.includes('crit chance')        ? 0.12 : 0; // Goblin War Horn
    const trophyAtkSpdPct  = hasTrophy('attack speed')                       ? 0.10 : 0; // Bloodfury Totem (party-wide)
    const trophyAllResist  = hasTrophy('all resist')                         ? 5    : 0; // Arcane Ward Stone

    let sRow = 0; // dynamic row counter for left stats column

    // Use the actual applied vitality HP stored on the hero (avoids double-counting since stats.maxHp already includes it)
    const hpBonus = (hero as any)._vitalityAppliedHp as number ?? 0;
    // Compact format for very large numbers so the stats column doesn't overflow
    const fmtBig = (n: number): string => {
      if (n >= 1_000_000) return (n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1).replace(/\.0$/, '') + 'M';
      if (n >= 100_000)   return (n / 1_000).toFixed(0) + 'k';
      return Math.floor(n).toLocaleString();
    };
    const hpStr = hpBonus > 0
      ? `${fmtBig(hero.stats.hp)}/${fmtBig(stats.maxHp)} (+${fmtBig(hpBonus)})`
      : `${fmtBig(hero.stats.hp)}/${fmtBig(stats.maxHp)}`;
    this.addStatLine(statsX, statsY + statGap * sRow++, 'HP', hpStr, '#ef4444');

    if (hero.heroClass === 'warrior') {
      this.addStatLine(statsX, statsY + statGap * sRow++, 'Rage', `${fmtBig(hero.stats.rage)}/${fmtBig(stats.maxRage)}`, '#ef4444');
    } else if (hero.heroClass === 'assassin') {
      this.addStatLine(statsX, statsY + statGap * sRow++, 'Energy', `${fmtBig(hero.stats.energy)}/${fmtBig(stats.maxEnergy)}`, '#eab308');
    } else {
      this.addStatLine(statsX, statsY + statGap * sRow++, 'Mana', `${fmtBig(hero.stats.mp)}/${fmtBig(stats.maxMp)}`, '#3b82f6');
    }

    const relicBonuses = getRelicPassiveBonuses(hero);
    const atk = Math.floor(stats.attack);
    // Final ATK shown first (what combat actually uses), with the breakdown of
    // flat-tome and % bonuses in parens so the player can see where the boost
    // came from. Combat formula: base + tome + base × (relic % + trophy %).
    {
      const atkPctSum = relicBonuses.attackPct + trophyAtkPct;
      const atkPctBonus = Math.floor(atk * atkPctSum);
      const finalAtk = atk + tDmg + atkPctBonus;
      const parts: string[] = [];
      if (tDmg > 0) parts.push(`+${tDmg}`);
      if (atkPctSum > 0) parts.push(`+${Math.round(atkPctSum * 100)}%`);
      const atkStr = parts.length > 0 ? `${fmtBig(finalAtk)} (${parts.join(', ')})` : `${fmtBig(finalAtk)}`;
      this.addStatLine(statsX, statsY + statGap * sRow++, 'Atk', atkStr, '#fbbf24');
    }

    // SpPwr — final value combat would use for a cast right now: (base + tome)
    // × (1 + relic spellPct). Boon Caster's Covenant / Archon's Focus / Lich
    // Form are situational and intentionally excluded from the panel — the
    // displayed number is the steady-state cast SP without proc/transient buffs.
    {
      const sp = Math.floor(stats.spellPower);
      const totalSpFlat = sp + tSp;
      if (totalSpFlat > 0) {
        const finalSp = Math.floor(totalSpFlat * (1 + relicBonuses.spellPct));
        const rPct = relicBonuses.spellPct > 0 ? Math.round(relicBonuses.spellPct * 100) : 0;
        const parts: string[] = [];
        if (tSp > 0) parts.push(`+${tSp}`);
        if (rPct > 0) parts.push(`+${rPct}%`);
        const spStr = parts.length > 0 ? `${fmtBig(finalSp)} (${parts.join(', ')})` : `${fmtBig(finalSp)}`;
        this.addStatLine(statsX, statsY + statGap * sRow++, 'SpPwr', spStr, '#5090c0');
      }
    }

    const def = Math.floor(stats.defense);
    const totalDef = def + tDef;
    const mitigation = Math.round(calculateMitigation(totalDef) * 100);
    const defStr = tDef > 0 ? `${totalDef} (${mitigation}%)` : `${def} (${mitigation}%)`;
    this.addStatLine(statsX, statsY + statGap * sRow++, 'Def', defStr, '#4080b0');

    // Combined speed bonuses — single parenthesised sum of tome% + relic%
    {
      const tPct = tSpd > 0 ? Math.round(tSpd * 100) : 0;
      const rPct = relicBonuses.speedPct > 0 ? Math.round(relicBonuses.speedPct * 100) : 0;
      const trPct = Math.round(trophyAtkSpdPct * 100);
      const totalPct = tPct + rPct + trPct;
      const spdStr = totalPct > 0
        ? `${stats.speed.toFixed(2)} (+${totalPct}%)`
        : `${stats.speed.toFixed(2)}`;
      this.addStatLine(statsX, statsY + statGap * sRow++, 'Spd', spdStr, '#22c55e');
    }

    const totalCrit = stats.critChance + tCrit + trophyCritPct;
    const critStr = (tCrit + trophyCritPct) > 0
      ? `${Math.round(totalCrit * 100)}%`
      : `${Math.round(stats.critChance * 100)}%`;
    this.addStatLine(statsX, statsY + statGap * sRow++, 'Crit', critStr, '#f97316');

    // Crit Damage — single parenthesised total (relic% + tome% combined, no wording)
    const relicCritDmg = relicBonuses.critDmgBonus;
    const totalCritDmg = stats.critDamage + relicCritDmg + tCritDmg;
    const combinedCdPct = Math.round((relicCritDmg + tCritDmg) * 100);
    const critDmgStr = combinedCdPct > 0
      ? `x${totalCritDmg.toFixed(2)} (+${combinedCdPct}%)`
      : `x${stats.critDamage.toFixed(2)}`;
    this.addStatLine(statsX, statsY + statGap * sRow++, 'CritDmg', critDmgStr, '#fb923c');

    // Resist line — inline under CritDmg (replaces the separate right-side panel)
    const sigAff = this.aggregateSigilAffixes();
    {
      // Arcane Ward Stone (+5% all resist) is a team-wide trophy; sigil affixes (per-element + allResist)
      // are also folded in so the displayed value matches what combat applies.
      // Shrine of Boons — per-school bonuses (Blessings of fire/frost/nature/shadow).
      // Matches combat's getHeroResist: min-clamped to −75 so a curse trade can
      // push a school negative but not completely expose it to DoT chip damage.
      // Elixir of Resistance — combat reads (hero as any)._resistanceBuff and
      // adds it school-agnostic, so reflect the same here.
      const elixirResist = Math.max(0, (hero as any)._resistanceBuff || 0);
      const fire   = Math.min(((stats as any).fireResist   ?? 0) + trophyAllResist + sigAff.fireResist   + sigAff.allResist + boonEff.fireResistBonus   + elixirResist, 75);
      const frost  = Math.min(((stats as any).frostResist  ?? 0) + trophyAllResist + sigAff.frostResist  + sigAff.allResist + boonEff.frostResistBonus  + elixirResist, 75);
      const shadow = Math.min(((stats as any).shadowResist ?? 0) + trophyAllResist + sigAff.shadowResist + sigAff.allResist + boonEff.shadowResistBonus + elixirResist, 75);
      const nature = Math.min(((stats as any).natureResist ?? 0) + trophyAllResist + sigAff.natureResist + sigAff.allResist + boonEff.natureResistBonus + elixirResist, 75);
      const ry = statsY + statGap * sRow++;
      const resLabel = this.add.text(statsX, ry, 'Resist:', {
        fontSize: '10px', fontFamily: 'Georgia, serif', color: '#6b4020', fontStyle: 'bold',
      }).setResolution(2);
      this.contentGroup.add(resLabel);
      // Four separate text objects — one per school — so each element can carry
      // its own colour without needing a rich-text plugin. Per user feedback the
      // emoji prefixes (🔥 ❄ 🌑 🌿) are removed; the column colour alone signals
      // the school, and a hover tooltip names + describes each element so the
      // shorthand stays unambiguous.
      const resStyle = { fontSize: '9px', fontFamily: 'Georgia, serif', fontStyle: 'bold' } as const;
      const colGap = 40;
      const SCHOOLS: Array<{
        key: 'fire' | 'frost' | 'shadow' | 'nature';
        value: number;
        color: string;
        label: string;
        tip: string;
      }> = [
        { key: 'fire',   value: fire,   color: '#f97316', label: 'Fire',   tip: 'Fire Resist\nMitigates burn DoTs and fire spell damage\n(Magmarath, Eruption, hellfire procs).' },
        { key: 'frost',  value: frost,  color: '#60a5fa', label: 'Frost',  tip: 'Frost Resist\nMitigates frost spell damage and slow chip\n(Frostgrasp Totem, Frost Nova, ice procs).' },
        { key: 'shadow', value: shadow, color: '#a78bfa', label: 'Shadow', tip: 'Shadow Resist\nMitigates shadow / void damage\n(Shadow Bolt, fear procs, void waves).' },
        { key: 'nature', value: nature, color: '#86efac', label: 'Nature', tip: 'Nature Resist\nMitigates nature / poison DoTs\n(Thornlash bleeds, vine snare, toxin procs).' },
      ];
      const created: Record<'fire' | 'frost' | 'shadow' | 'nature', Phaser.GameObjects.Text> = {} as any;
      SCHOOLS.forEach((school, i) => {
        const t = this.add.text(statsX + 90 + colGap * i, ry, `${school.value}%`, {
          ...resStyle, color: school.color,
        }).setResolution(2).setInteractive({ useHandCursor: false });
        this.contentGroup.add(t);
        // Hover tooltip — Phaser-drawn dark panel with the element name + what it resists.
        let tip: Phaser.GameObjects.Text | null = null;
        t.on('pointerover', () => {
          tip = this.add.text(t.x + 10, t.y - 4, school.tip, {
            fontSize: '9px', fontFamily: 'Georgia, serif', color: school.color,
            backgroundColor: '#0d0a08', padding: { x: 8, y: 6 }, lineSpacing: 3,
          }).setResolution(2).setOrigin(0, 1).setDepth(60);
          this.contentGroup.add(tip);
        });
        t.on('pointerout', () => { tip?.destroy(); tip = null; });
        created[school.key] = t;
      });
      this.resistLine = created;
    }

    // Sigil summary line removed per user request — was eating 1-3 rows of
    // panel space when a player had multiple sigil affixes equipped. The
    // effects still apply in combat (read via aggregateSigilAffixes elsewhere
    // and BattleScene.getSigilEffects); the per-sigil tooltips on the
    // vertical strip already surface what each one does on hover.

    // ── Rune Tree bonuses ─────────────────────────────────────────────────────
    // (pb already computed above for stat layering — reuse it here)
    const runeLines: string[] = [];
    if (pb.atk > 0)    runeLines.push(`+${Math.floor(pb.atk)} ATK`);
    if (pb.crit > 0)   runeLines.push(`+${Math.round(pb.crit * 100)}% Crit`);
    if (pb.critDmg > 0) runeLines.push(`+${Math.round(pb.critDmg * 100)}% CritDmg`);
    if (pb.atkSpd > 0) runeLines.push(`+${Math.round(pb.atkSpd * 100)}% AtkSpd`);
    if (pb.sp > 0)     runeLines.push(`+${Math.floor(pb.sp)} SpPwr`);
    if (pb.mp > 0)     runeLines.push(`+${Math.floor(pb.mp)} Mana`);
    if (pb.cdr > 0)    runeLines.push(`-${Math.round(pb.cdr * 100)}% CD`);
    if (pb.dmgPct > 0) runeLines.push(`+${Math.round(pb.dmgPct * 100)}% Dmg`);
    if (pb.goldPct > 0) runeLines.push(`+${Math.round(pb.goldPct * 100)}% Gold`);
    if (pb.keystoneBerserk)     runeLines.push('ᚹ Berserk Rite');
    if (pb.keystoneDivineWrath) runeLines.push('ᚨ Divine Wrath');
    if (pb.keystoneAncientPact) runeLines.push('ᚠ Ancient Pact');
    // Rune Tree — TOP-RIGHT, generous gap below the ability row. Breaks onto a new line after every 3 stats.
    if (runeLines.length > 0) {
      const RUNES_X = 285;
      const RUNES_Y = 170; // was 140 — wider gap from the ability row
      const runeIcon = this.add.text(RUNES_X, RUNES_Y, 'ᚠ', {
        fontSize: '14px', fontFamily: "'Cinzel Decorative', 'Palatino Linotype', Georgia, serif",
        color: '#d4a84a', fontStyle: 'bold', stroke: '#1a0f00', strokeThickness: 2,
      });
      this.contentGroup.add(runeIcon);
      // Chunk rune lines into rows of 3 so long lists wrap onto multiple short lines rather than one wide run.
      const runeRows: string[] = [];
      for (let i = 0; i < runeLines.length; i += 3) {
        runeRows.push(runeLines.slice(i, i + 3).join('  '));
      }
      const runeText = this.add.text(RUNES_X + 18, RUNES_Y + 2, runeRows.join('\n'), {
        fontSize: '9px', fontFamily: 'Georgia, serif', color: '#c8973a', lineSpacing: 3,
        wordWrap: { width: 180 },
      });
      this.contentGroup.add(runeText);
    }

    // Abilities — TOP-RIGHT. Align first icon's left edge with the rune icon (x=285) below.
    // Icon center sits at (abilityX + 14), width 30 → left edge = abilityX - 1; so abilityX = 286.
    // ABILITY_GAP is icon center-to-center spacing (icons are ~30px wide, so 40 leaves a visible gap).
    const abilityX = 286;
    const abilityY = 60;
    const ABILITY_GAP = 40;
    const abilityCount = 4;
    const abilityCenterX = abilityX + ((abilityCount - 1) * ABILITY_GAP) / 2;
    // Total talent points (X/12) — CENTERED ABOVE the ability row
    const totalSpent = getSpentTalentPoints(hero.talentBonuses ?? {});
    const talentTotalLabel = this.add.text(abilityCenterX, abilityY,
      `${totalSpent}/${MAX_TALENT_POINTS} pts`, {
      fontSize: '10px', fontFamily: 'Georgia, serif',
      color: totalSpent >= MAX_TALENT_POINTS ? '#ef4444' : totalSpent > 0 ? '#c8973a' : '#6b4020',
    }).setOrigin(0.5, 0).setDepth(5);
    this.contentGroup.add(talentTotalLabel);
    const ABILITY_COLORS: Record<string, number> = {
      aoe: 0xef4444, dot: 0x22c55e, slow: 0x3b82f6, heal: 0x10b981,
      buff: 0xfbbf24, stun: 0xeab308, fear: 0x4090c0, resurrect: 0xcc3322, damage: 0xf97316,
      fireball: 0xf97316, magma_shield: 0xf97316, hot: 0x10b981, reflect: 0x4090c0,
      frost_shield: 0x3b82f6, magma_shield_old: 0xf97316, evasion: 0xeab308,
      passive_resurrect: 0xef4444, passive_loot: 0xfbbf24,
      stone_form: 0x94a3b8, thunder_strike: 0xfbbf24, mace_throw: 0xd97706,
      jump_strike: 0xf472b6, freeze: 0x93c5fd, war_cry: 0xfbbf24, passive_spear: 0xf97316,
    };
    // Helper: true for abilities that are passive and cannot be talent-upgraded
    const isPassiveAbility = (ability: { effectType: string; maxLevel: number; id: string }) =>
      ability.maxLevel <= 1 && !getTalentUpgradeInfo(ability.id);
    hero.abilities.forEach((ability, i) => {
      const ax = abilityX + i * ABILITY_GAP;
      const ay = abilityY + 28;
      const iconColor = ABILITY_COLORS[ability.effectType] || 0x6b7280;
      const talentPts = hero.talentBonuses?.[ability.id] || 0;

      // Buff active check — stone_form turns icon dark while running
      const stoneFormOn = ability.id === 'stone_form' && (hero as any)._stoneFormActive === true;
      const onCooldown = ability.currentCooldown > 0;
      const activeBuff = stoneFormOn; // extend here for future buffs

      // Use spell icon sprite if available, otherwise colored rectangle fallback
      let icon: Phaser.GameObjects.GameObject;
      if (ability.iconKey && this.textures.exists(ability.iconKey)) {
        const borderColor = activeBuff ? 0x6b7280 : iconColor;
        const bg = this.add.rectangle(ax + 14, ay + 10, 30, 30, activeBuff ? 0x0f1117 : 0x1e293b, activeBuff ? 0.9 : 0.6)
          .setStrokeStyle(1, borderColor);
        const img = this.add.image(ax + 14, ay + 10, ability.iconKey)
          .setDisplaySize(26, 26).setInteractive();
        // Darken icon when buff is active (stone form = grey tint), dim when on cooldown
        if (activeBuff) {
          img.setTint(0x6b7280); // grey — buff running
        } else if (onCooldown) {
          img.setAlpha(0.45);    // dim — cooling down
        }
        this.contentGroup.add(bg);
        this.contentGroup.add(img);
        icon = img;
      } else {
        const rect = this.add.rectangle(ax + 14, ay + 10, 30, 20, iconColor, 0.8)
          .setStrokeStyle(1, 0xffffff).setInteractive();
        const iconLabel = this.add.text(ax + 14, ay + 10, ability.name.slice(0, 3).toUpperCase(), {
          fontSize: '9px', fontFamily: 'Georgia, serif', color: '#ffffff',
        }).setOrigin(0.5);
        this.contentGroup.add(rect);
        this.contentGroup.add(iconLabel);
        icon = rect;
      }

      // Always show rank badge below icon for non-passives
      if (!isPassiveAbility(ability)) {
        const talentMaxRank = getTalentUpgradeInfo(ability.id)?.maxRank ?? ability.maxLevel;
        const badgeText = talentPts > 0 ? `${talentPts}/${talentMaxRank}` : `Lv${ability.level}`;
        const badgeColor = talentPts > 0 ? '#c8973a' : '#7a9a7a';
        const rankBadge = this.add.text(ax + 14, ay + 29, badgeText, {
          fontSize: '10px', fontFamily: 'Georgia, serif', color: badgeColor, fontStyle: 'bold',
          backgroundColor: '#0d0a0e', padding: { x: 3, y: 1 },
        }).setOrigin(0.5, 0).setDepth(5);
        this.contentGroup.add(rankBadge);
      }

      let tooltip: Phaser.GameObjects.Container | Phaser.GameObjects.Text | null = null;
      icon.on('pointerover', () => {
        tooltip?.destroy(); tooltip = null;
        const costLabel = hero.heroClass === 'warrior' ? 'Rage' : hero.heroClass === 'assassin' ? 'Energy' : 'Mana';
        const talentInfo = getTalentUpgradeInfo(ability.id);
        let talentLine = '';
        if (talentPts > 0 && talentInfo) {
          const currentTotal = getTalentTotalLabel(ability.id, talentPts);
          const nextRank = talentPts < ability.maxLevel
            ? `Next rank: ${getNextRankLabel(ability.id, talentPts)}`
            : 'Max rank reached';
          if (talentPts >= (talentInfo.maxRank ?? Infinity)) {
            talentLine = `Talent (rank ${talentPts}): ${currentTotal || talentInfo.label}`;
          } else if (talentInfo.maxRank === 1) {
            talentLine = `Talent: ${talentInfo.label}`;
          } else {
            talentLine = `Talent (rank ${talentPts}): ${currentTotal}\n${nextRank}`;
          }
        } else if (talentInfo && !talentInfo.maxRank) {
          talentLine = `Next rank: ${getNextRankLabel(ability.id, 0)}`;
        }
        // Arcane Might: show resist bonus detail (1% base + 1% per talent rank)
        const arcaneSPLine = ability.id === 'arcane_might'
          ? `✦ ${1 + talentPts}% all resist for 10s (base 1% + ${talentPts}% talent)`
          : '';
        const passive = isPassiveAbility(ability);
        const titleSuffix = passive
          ? ' (Passive)'
          : talentPts > 0 ? ` (${talentPts}/${getTalentUpgradeInfo(ability.id)?.maxRank ?? ability.maxLevel})` : ` (Lv${ability.level})`;
        const lines = [
          `${ability.name}${titleSuffix}`,
          ability.description,
          ...(!passive ? [`Cost: ${ability.manaCost} ${costLabel} | CD: ${(ability.cooldown / 1000).toFixed(0)}s`] : []),
          ...(talentLine ? [talentLine] : []),
          ...(arcaneSPLine ? [arcaneSPLine] : []),
        ];
        // Position above icon, clamped so tooltip never exits the 480px viewport horizontally
        // or the scene vertically. If the tooltip would clip past the top (beyond the header),
        // flip it so it sits BELOW the icon instead.
        const ttx = Math.min(ax, 480 - 236);
        // Build above-anchored first; measure; flip below if it would clip past the header.
        tooltip = createHudTooltip(this, ttx, ay - 2, lines.join('\n'),
          0, 1, { fontSize: '11px', wrap: 220, depth: 100 });
        const TOP_SAFE_Y = 52;
        if ((ay - 2) - tooltip.height < TOP_SAFE_Y) {
          tooltip.destroy();
          tooltip = createHudTooltip(this, ttx, ay + 38, lines.join('\n'),
            0, 0, { fontSize: '11px', wrap: 220, depth: 100 });
        }
        this.contentGroup.add(tooltip);
      });
      icon.on('pointerout', () => {
        tooltip?.destroy();
        tooltip = null;
      });
    });

    // ── MIDDLE band: Equipment — weapons LEFT, relics RIGHT (no headers) ──
    const equipX = 25;
    const equipY = 245;

    const equipSlots: EquipmentSlot[] = ['weapon1', 'weapon2', 'armor', 'reserved', 'relic1', 'relic2', 'relic3', 'relic4'];
    const slotNames: Record<EquipmentSlot, string> = {
      weapon1: 'Wp1', weapon2: 'Wp2', armor: 'Arm', reserved: 'Tro',
      relic1: 'Rl1', relic2: 'Rl2', relic3: 'Rl3', relic4: 'Rl4',
    };

    const RARITY_ICON_COLORS: Record<string, number> = {
      common: 0x9ca3af, uncommon: 0x22c55e, rare: 0x3b82f6, epic: 0xcc3322, legendary: 0xf97316,
    };

    // Two columns of equipment — weapons LEFT, relics RIGHT, flanking center sprite
    // More vertical spacing per request (row * 38 vs 34)
    equipSlots.forEach((slot, i) => {
      const col = i < 4 ? 0 : 1;
      const row = i % 4;
      const ex = col === 0 ? equipX : 340;
      const ey = equipY + 8 + row * 38;
      const item = hero.equipment[slot];

      // Slot-type short labels (Wp1/Rl1/etc) removed — item name has full column width now
      if (slot === 'reserved' && !item) {
        const trophyText = this.add.text(ex + 22, ey, '[ Trophy ]', {
          fontSize: '9px', fontFamily: 'Georgia, serif', color: '#3a2800',
        });
        this.contentGroup.add(trophyText);
      } else if (item) {
        const isRelic = slot.startsWith('relic');
        const isLocked = isRelic && (this.gsm.player.lockedRelics ?? []).includes(item.id);

        let icon: Phaser.GameObjects.GameObject;
        if (item.spriteKey && this.textures.exists(item.spriteKey)) {
          icon = this.add.image(ex + 10, ey + 8, item.spriteKey)
            .setDisplaySize(18, 18).setInteractive();
        } else {
          const iconColor = RARITY_ICON_COLORS[item.rarity] || 0x6b7280;
          icon = this.add.rectangle(ex + 10, ey + 8, 14, 14, iconColor, 0.9)
            .setStrokeStyle(1, 0xffffff).setInteractive();
        }
        this.contentGroup.add(icon);

        // Lock badge on locked relics — shown to the LEFT of the item icon
        if (isLocked) {
          const lockBadge = this.add.text(ex - 2, ey + 3, '🔒', {
            fontSize: '9px', padding: { x: 0, y: 0 },
          }).setOrigin(1, 0).setDepth(6);
          this.contentGroup.add(lockBadge);
        }

        // Stack badge for stackable relics — each item carries its OWN runtimeStacks counter.
        if (isRelic) {
          let stackLabel = '';
          const rs = item.runtimeStacks ?? 0;
          if (item.name === 'Crescendo Idol' && rs > 0) {
            stackLabel = `${rs}`;
          } else if (item.name === 'Bloodtally Knife' && rs > 0) {
            stackLabel = `${rs}%`;
          } else if (item.name === 'Vitality Idol' && rs > 0) {
            stackLabel = `${rs}`;
          } else if (item.name === "Archon's Focus") {
            // Always show the cap so the player can see the roll even before
            // stacking, e.g. "3/82" on a freshly equipped legendary roll.
            const cap = item.maxStacksRoll ?? 50;
            stackLabel = `${rs}/${cap}`;
          }
          if (stackLabel) {
            const stackBadge = this.add.text(ex + 10, ey + 17, stackLabel, {
              fontSize: '9px', fontFamily: 'Georgia, serif', color: '#fde68a',
              fontStyle: 'bold', backgroundColor: '#0a0500', padding: { x: 2, y: 1 },
            }).setOrigin(0.5, 1).setDepth(7);
            this.contentGroup.add(stackBadge);
          }
        }

        const color = RARITY_COLORS[item.rarity];
        const levelSuffix = item.rarity === 'unique' ? '' : ` Lv${item.level}`;
        const itemText = this.add.text(ex + 22, ey, `${item.name}${levelSuffix}`, {
          fontSize: '9px', fontFamily: 'Georgia, serif', color,
          wordWrap: { width: 95 }, // wider now that slot label is gone (~117px total)
        }).setInteractive({ useHandCursor: true });
        this.contentGroup.add(itemText);
        // Forward hover events from text → icon so the tooltip also shows when hovering name
        itemText.on('pointerover', () => icon.emit('pointerover'));
        itemText.on('pointerout',  () => icon.emit('pointerout'));

        const bonusEntries = Object.entries(item.statBonuses).filter(([, v]) => v && v !== 0);
        const bonusParts = bonusEntries.slice(0, 3).map(([k, v]) => formatItemBonus(k, v as number));
        if (bonusEntries.length > 3) bonusParts.push(`+${bonusEntries.length - 3}…`);
        // Show up to 3 stats (with wordWrap) so a resist roll isn't silently hidden.
        const bonusText = this.add.text(ex + 22, ey + 18, bonusParts.join(' '), {
          fontSize: '8px', fontFamily: 'Georgia, serif', color: '#5a3600',
          wordWrap: { width: 95 },
        });
        this.contentGroup.add(bonusText);

        // Mouseover tooltip for equipped item
        icon.on('pointerover', () => {
          this.equipTooltip?.destroy();
          const liveItem = this.gsm.player.heroes[this.selectedHeroIndex]?.equipment[slot];
          if (!liveItem) return;
          const statLines = formatEquipmentTooltipLines(liveItem);
          const archStr = (liveItem.armorArchetype || liveItem.armorSubtype)
            ? getArmorTypeLabel(liveItem)
            : (liveItem.weaponType ? getWeaponTypeLabel(liveItem) : '');
          const isRelicItem = liveItem.slot.startsWith('relic');
          const effectLine = isRelicItem
            ? (getRelicScaledEffect(liveItem.name, liveItem.level, liveItem.maxStacksRoll) || liveItem.specialEffect || '')
            : (liveItem.specialEffect || '');
          const levelStr = liveItem.rarity === 'unique' ? '' : ` · Lv${liveItem.level}`;
          const lockNote = isRelicItem && (this.gsm.player.lockedRelics ?? []).includes(liveItem.id)
            ? '[LOCKED — Ctrl+click to unlock]'
            : (isRelicItem ? '[Ctrl+click to lock]' : '[Double-click to unequip]');
          const bottom = [`Sell: ${liveItem.sellValue}g`, lockNote];
          // Relics are class-agnostic so they don't use the "Can equip on ..." accent.
          // Instead we surface a plain "Relic" tag in the classLine slot so the
          // tooltip reads as a relic at a glance (otherwise the bottom section
          // just showed sell + lock hint with no type callout).
          const classLine = isRelicItem
            ? { text: 'Relic — fits any class', color: '#a07840' }
            : buildEquipStatusAccent(liveItem, this.gsm.player.heroes, hero);
          const allStatLines = effectLine
            ? [...statLines, `★ ${effectLine}`]
            : statLines;
          const tx = Math.min(ex, 480 - 256);
          const openBelow = row < 2;
          const ty = openBelow ? ey + 22 : ey - 2;
          const originY = openBelow ? 0 : 1;
          this.equipTooltip = createItemTooltip(this, tx, ty, {
            name: `${liveItem.name}${levelStr}`,
            nameColor: RARITY_COLORS[liveItem.rarity],
            statLines: allStatLines,
            bottomLines: bottom,
            classLine,
            typeLabel: archStr || undefined,
            fontSize: '10px', wrap: 240, depth: 150,
          }, 0, originY);
        });
        icon.on('pointerout', () => {
          this.equipTooltip?.destroy();
          this.equipTooltip = null;
        });
        // Double-click to unequip (non-locked) / Ctrl+click to toggle lock (relics)
        let lastClickTime = 0;
        icon.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
          if (isRelic && pointer.leftButtonDown() && pointer.event.ctrlKey) {
            // Ctrl+click: toggle lock
            pointer.event.preventDefault();
            const locked = this.gsm.player.lockedRelics ?? [];
            if (locked.includes(item.id)) {
              this.gsm.player.lockedRelics = locked.filter((id) => id !== item.id);
            } else {
              this.gsm.player.lockedRelics = [...locked, item.id];
            }
            this.refreshContent();
            return;
          }
          if (!isLocked) {
            const now = Date.now();
            if (now - lastClickTime < 300) {
              // Double-click: unequip
              this.equipTooltip?.destroy();
              this.equipTooltip = null;
              this.gsm.unequipItem(this.selectedHeroIndex, slot);
              this.refreshContent();
            }
            lastClickTime = now;
          }
        });
      } else {
        // Relic slots 3 & 4 are quest-gated — Phaser-drawn padlock glyph + "[ Locked ]"
        // label, with a tooltip pointing at the gate quest. (No emoji — project rule.)
        const isRelicSlotGated = slot === 'relic3' || slot === 'relic4';
        const unlocked = !isRelicSlotGated || this.gsm.isRelicSlotUnlocked(slot as 'relic3' | 'relic4');
        if (isRelicSlotGated && !unlocked) {
          const lockGfx = this.add.graphics();
          drawPadlock(lockGfx, ex + 10, ey + 6, 0x7a4a18);
          this.contentGroup.add(lockGfx);
          const gateText = this.add.text(ex + 22, ey, '[ Locked ]', {
            fontSize: '9px', fontFamily: 'Georgia, serif', color: '#5a3010',
          }).setInteractive({ useHandCursor: false });
          const questName = slot === 'relic3' ? 'Veil of the Shadow Realm' : 'Rite of the Void Elves';
          const slotNum = slot === 'relic3' ? 3 : 4;
          gateText.on('pointerover', () => {
            this.equipTooltip?.destroy();
            // Build the tooltip first, then clamp its position so it can't fall off the
            // right or bottom edge of the 480×576 scene. Previously it was anchored at
            // (ex+22, ey+12) with origin (0,0) — for right-column slots (ex=340) that
            // pushed the right half of the tooltip off-screen.
            const tip = this.add.text(0, 0, `Relic Slot ${slotNum}\nComplete "${questName}"`, {
              fontSize: '9px', fontFamily: 'Georgia, serif', color: '#c8973a',
              backgroundColor: '#0d0a0e', padding: { x: 6, y: 4 },
              wordWrap: { width: 180 }, align: 'center',
            }).setDepth(50);
            const margin = 6;
            const sceneW = 480;
            const sceneH = 576;
            let tx = ex + 22;
            let ty = ey + 12;
            if (tx + tip.width > sceneW - margin) tx = sceneW - margin - tip.width;
            if (tx < margin) tx = margin;
            if (ty + tip.height > sceneH - margin) ty = ey - tip.height - 4;
            tip.setPosition(tx, ty);
            this.equipTooltip = tip;
          });
          gateText.on('pointerout', () => { this.equipTooltip?.destroy(); this.equipTooltip = null; });
          this.contentGroup.add(gateText);
        } else {
          const emptyText = this.add.text(ex + 22, ey, '[ Empty ]', {
            fontSize: '9px', fontFamily: 'Georgia, serif', color: '#3a2800',
          });
          this.contentGroup.add(emptyText);
        }
      }
    });

    // ── BOTTOM section: Quick equip (lowered so filters clear the Resist row above) ──
    const invHeaderY = 410;

    // Filter buttons
    const filters: Array<{ label: string; key: 'all' | 'weapon' | 'armor' | 'relic' | 'trophy' | 'sigil' }> = [
      { label: 'All', key: 'all' },
      { label: 'Wpn', key: 'weapon' },
      { label: 'Arm', key: 'armor' },
      { label: 'Rel', key: 'relic' },
      { label: 'Tro', key: 'trophy' },
      { label: 'Sig', key: 'sigil' },
    ];
    filters.forEach((f, i) => {
      const isActive = this.inventoryFilter === f.key;
      const bx = equipX + i * 36 + 16;
      const by = invHeaderY + 14;
      const fbg = this.add.rectangle(bx, by, 32, 14, isActive ? 0x1a0f06 : 0x0d0a08, 1)
        .setStrokeStyle(1, isActive ? 0xc8973a : 0x5a3600)
        .setInteractive({ useHandCursor: true });
      const ftxt = this.add.text(bx, by, f.label, {
        fontSize: '9px', fontFamily: 'Georgia, serif',
        color: isActive ? '#c8973a' : '#6b4020',
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });
      this.contentGroup.add(fbg);
      this.contentGroup.add(ftxt);
      const doFilter = () => { this.inventoryFilter = f.key; this.inventoryScrollOffset = 0; this.refreshContent(); };
      fbg.on('pointerdown', doFilter);
      ftxt.on('pointerdown', doFilter);
      fbg.on('pointerover', () => { ftxt.setColor('#c8973a'); fbg.setStrokeStyle(1, 0xc8973a); });
      fbg.on('pointerout', () => { ftxt.setColor(isActive ? '#c8973a' : '#6b4020'); fbg.setStrokeStyle(1, isActive ? 0xc8973a : 0x5a3600); });
    });

    const invItemsY = invHeaderY + 26;
    const invItemH = 18;

    // Sigil filter branches to a dedicated renderer and skips the equipment list.
    if (this.inventoryFilter === 'sigil') {
      this.renderSigilInventory(equipX, invItemsY, invItemH);
      return;
    }

    // Filter inventory items — only show items this hero can actually equip
    let allAvailable = this.gsm.player.inventory.filter((item) => {
      // Class restriction check
      if (item.classRestriction && item.classRestriction !== hero.heroClass) return false;
      // Weapon type restriction
      if (item.weaponType && WEAPON_CLASS_RESTRICTIONS[item.weaponType]) {
        if (!WEAPON_CLASS_RESTRICTIONS[item.weaponType].includes(hero.heroClass)) return false;
      }
      // Armor archetype restriction
      if (item.armorArchetype && ARMOR_CLASS_RESTRICTIONS[item.armorArchetype]) {
        if (!ARMOR_CLASS_RESTRICTIONS[item.armorArchetype].includes(hero.heroClass)) return false;
      }
      return true;
    });

    if (this.inventoryFilter === 'weapon') {
      allAvailable = allAvailable.filter((item) => item.slot.startsWith('weapon'));
    } else if (this.inventoryFilter === 'armor') {
      allAvailable = allAvailable.filter((item) => item.slot === 'armor');
    } else if (this.inventoryFilter === 'relic') {
      allAvailable = allAvailable.filter((item) => item.slot.startsWith('relic'));
    } else if (this.inventoryFilter === 'trophy') {
      allAvailable = allAvailable.filter((item) => item.slot === 'reserved');
    }

    if (this.inventorySearch) {
      allAvailable = allAvailable.filter((item) =>
        item.name.toLowerCase().includes(this.inventorySearch),
      );
    }

    // Sort by item level descending (highest first)
    allAvailable.sort((a, b) => b.level - a.level);

    this.inventoryScrollOffset = Math.min(
      this.inventoryScrollOffset,
      Math.max(0, allAvailable.length - this.ITEMS_PER_PAGE),
    );
    const page = allAvailable.slice(this.inventoryScrollOffset, this.inventoryScrollOffset + this.ITEMS_PER_PAGE);

    page.forEach((item, i) => {
      const y = invItemsY + i * invItemH;
      const color = RARITY_COLORS[item.rarity];
      const levelOk = item.levelRequirement <= hero.level;
      const levelSuffix2 = item.rarity === 'unique' ? '' : ` Lv${item.level}`;

      // Item icon — use sprite if available, else colored rarity square
      let iconObj: Phaser.GameObjects.Image | Phaser.GameObjects.Rectangle;
      if (item.spriteKey && this.textures.exists(item.spriteKey)) {
        iconObj = this.add.image(equipX + 9, y + 7, item.spriteKey)
          .setDisplaySize(14, 14).setAlpha(levelOk ? 1 : 0.45);
      } else {
        const rarityHex = Phaser.Display.Color.HexStringToColor(color).color;
        iconObj = this.add.rectangle(equipX + 9, y + 7, 12, 12, rarityHex, levelOk ? 0.85 : 0.3)
          .setStrokeStyle(1, 0x5a3600);
      }
      iconObj.setInteractive({ useHandCursor: true });
      this.contentGroup.add(iconObj);

      // Label — item name + Lv only (no slot type)
      const labelText = `${item.name}${levelSuffix2}${levelOk ? '' : ` (Req${item.levelRequirement})`}`;
      const btn = this.add.text(equipX + 22, y, labelText, {
        fontSize: '9px', fontFamily: 'Georgia, serif', color: levelOk ? color : '#5a3600',
        backgroundColor: '#0d0a0e', padding: { x: 3, y: 1 },
      }).setInteractive({ useHandCursor: true });
      this.contentGroup.add(btn);

      // Forward clicks/hover from icon to btn for consistent interaction
      iconObj.on('pointerover', () => btn.emit('pointerover'));
      iconObj.on('pointerout', () => btn.emit('pointerout'));
      iconObj.on('pointerdown', () => btn.emit('pointerdown'));

      // Double-click support: skip the slot picker and equip to the first valid slot.
      // For relics → relic1; for weapons → weapon1 (main-hand items) or weapon2 (off-hand-only).
      // Single click on items with multiple candidate slots keeps the existing slot-picker flow.
      let lastClickTime = 0;
      const equipToFirstSlot = () => {
        if (item.slot.startsWith('relic')) {
          this.gsm.equipItem(this.selectedHeroIndex, item, 'relic1');
        } else if (item.slot.startsWith('weapon')) {
          const isOffhand = item.weaponType === 'shield' || item.weaponType === 'focus';
          this.gsm.equipItem(this.selectedHeroIndex, item, isOffhand ? 'weapon2' : 'weapon1');
        } else if (item.slot === 'reserved') {
          this.gsm.equipItem(this.selectedHeroIndex, item, 'reserved');
        } else {
          this.gsm.equipItem(this.selectedHeroIndex, item);
        }
        this.refreshContent();
      };

      btn.on('pointerdown', () => {
        if (!levelOk) return;
        const now = Date.now();
        const isDoubleClick = now - lastClickTime < 300;
        lastClickTime = now;
        if (isDoubleClick) {
          equipToFirstSlot();
          return;
        }
        // Relics/weapons: show slot picker if multiple slots available. Quest-gated relic
        // slots (3/4) are filtered out so the player only sees slots they can actually use.
        if (item.slot.startsWith('relic')) {
          const relicSlots = (['relic1', 'relic2', 'relic3', 'relic4'] as const)
            .filter((s) => this.gsm.isRelicSlotUnlocked(s));
          this.showSlotPicker(item, relicSlots as EquipmentSlot[], hero);
        } else if (item.slot.startsWith('weapon')) {
          // Shield/focus are off-hand only; 2H weapons/wands are main-hand only; others can go in either.
          // Runewhisper is unique: weapon1-only — Runic Surge only fires on the main-hand swing,
          // so showing a Weapon 2 row is a UX trap (the equip would be silently rejected by GSM).
          const isOffhand = item.weaponType === 'shield' || item.weaponType === 'focus';
          const isMainHandOnly = !!item.weaponType && TWO_HANDED_WEAPON_TYPES.includes(item.weaponType) || item.weaponType === 'wand';
          const isRunewhisper = item.name === 'Runewhisper';
          const weaponSlots: EquipmentSlot[] = isOffhand ? ['weapon2']
            : (isMainHandOnly || isRunewhisper) ? ['weapon1']
            : ['weapon1', 'weapon2'];
          this.showSlotPicker(item, weaponSlots, hero);
        } else if (item.slot === 'reserved') {
          // Trophy: equip directly to reserved slot (swaps out old trophy if present)
          this.gsm.equipItem(this.selectedHeroIndex, item, 'reserved');
          this.refreshContent();
        } else {
          this.gsm.equipItem(this.selectedHeroIndex, item);
          this.refreshContent();
        }
      });

      // Mouseover tooltip for inventory item — Shift+hold shows comparison with equipped.
      // The tooltip now uses createItemTooltip for the standard layout; shift-compare
      // lines ride in the bottomLines section so they get the bronze-dim colour.
      const showInvTooltip = () => {
        this.equipTooltip?.destroy();
        const statLines = formatEquipmentTooltipLines(item);
        const archStr = (item.armorArchetype || item.armorSubtype)
          ? getArmorTypeLabel(item)
          : (item.weaponType ? getWeaponTypeLabel(item) : '');
        const isRelic = item.slot.startsWith('relic');
        const invEffectLine = isRelic
          ? (getRelicScaledEffect(item.name, item.level, item.maxStacksRoll) || item.specialEffect || '')
          : (item.specialEffect || '');
        const levelStr2 = item.rarity === 'unique' ? '' : ` · Lv${item.level}`;

        const allStatLines = invEffectLine ? [...statLines, `★ ${invEffectLine}`] : statLines;
        const bottom: string[] = [`Sell: ${item.sellValue}g`];
        if (this.shiftHeld) {
          // Resolve the best compare target. `item.slot` is the item's NATIVE slot (e.g. a
          // dagger always has 'weapon1'), but the hero might have a weapon in weapon2 or
          // nothing in weapon1. For weapons / relics we scan the category and compare
          // against the first non-empty slot — so the tooltip shows a meaningful diff.
          const pickCompareTarget = (): Equipment | null => {
            if (item.slot.startsWith('weapon')) {
              return hero.equipment.weapon1 ?? hero.equipment.weapon2 ?? null;
            }
            if (item.slot.startsWith('relic')) {
              return hero.equipment.relic1
                ?? hero.equipment.relic2
                ?? hero.equipment.relic3
                ?? hero.equipment.relic4
                ?? null;
            }
            return hero.equipment[item.slot as EquipmentSlot] ?? null;
          };
          const equippedHere = pickCompareTarget();
          bottom.push('─── vs. Equipped ───');
          if (equippedHere) {
            bottom.push(`${equippedHere.name}${equippedHere.rarity === 'unique' ? '' : ` Lv${equippedHere.level}`}`);
            const allKeys = new Set([
              ...Object.keys(item.statBonuses),
              ...Object.keys(equippedHere.statBonuses),
            ]);
            let anyDiff = false;
            for (const k of allKeys) {
              const newV = (item.statBonuses as Record<string, number>)[k] || 0;
              const oldV = (equippedHere.statBonuses as Record<string, number>)[k] || 0;
              const delta = newV - oldV;
              if (delta === 0) continue;
              anyDiff = true;
              const arrow = delta > 0 ? '▲' : '▼';
              const absD = Math.abs(delta);
              const disp = absD < 1 ? `${(absD * 100).toFixed(1)}%` : `${Math.round(absD * 100) / 100}`;
              bottom.push(`  ${arrow} ${disp} ${k}`);
            }
            if (!anyDiff) bottom.push('  (identical stats)');
          } else {
            bottom.push('  (slot is empty)');
          }
        } else {
          bottom.push('[Hold Shift to compare]');
        }
        const classLine = isRelic
          ? { text: 'Relic — fits any class', color: '#a07840' }
          : buildEquipStatusAccent(item, this.gsm.player.heroes, hero);
        this.currentTooltipPos = { x: Math.min(equipX, 480 - 256), y: y - 4 };
        this.equipTooltip = createItemTooltip(this, this.currentTooltipPos.x, this.currentTooltipPos.y, {
          name: `${item.name}${levelStr2}`,
          nameColor: RARITY_COLORS[item.rarity],
          statLines: allStatLines,
          bottomLines: bottom,
          classLine,
          typeLabel: archStr || undefined,
          fontSize: '10px', wrap: 240, depth: 150,
        }, 0, 1);
      };
      this.currentTooltipBuilder = showInvTooltip;
      btn.on('pointerover', showInvTooltip);
      btn.on('pointerout', () => {
        this.equipTooltip?.destroy();
        this.equipTooltip = null;
        this.currentTooltipBuilder = null;
      });
    });

    if (allAvailable.length > this.ITEMS_PER_PAGE) {
      const scrollInfo = this.add.text(
        equipX,
        invItemsY + this.ITEMS_PER_PAGE * invItemH,
        `${this.inventoryScrollOffset + 1}-${Math.min(this.inventoryScrollOffset + this.ITEMS_PER_PAGE, allAvailable.length)} / ${allAvailable.length}`,
        { fontSize: '9px', fontFamily: 'Georgia, serif', color: '#5a3600' },
      );
      this.contentGroup.add(scrollInfo);
    }
  }

  /** Show a slot picker popup for relics/weapons — user chooses which slot to equip into */
  private showSlotPicker(item: Equipment, slots: EquipmentSlot[], hero: Hero): void {
    const width = 480, height = GAME_H;
    const overlay = this.add.rectangle(width / 2, height / 2, width, height, 0x000000, 0.6)
      .setDepth(200).setInteractive();
    const slotNames: Record<string, string> = {
      weapon1: 'Weapon 1', weapon2: 'Weapon 2',
      relic1: 'Relic 1', relic2: 'Relic 2', relic3: 'Relic 3', relic4: 'Relic 4',
    };

    const panelH = 40 + slots.length * 32;
    const bg = this.add.rectangle(width / 2, height / 2, 260, panelH, 0x0d0a08, 0.98)
      .setStrokeStyle(2, 0x5a3600).setDepth(201);
    const bgInner = this.add.rectangle(width / 2, height / 2, 254, panelH - 6, 0x0d0a08, 0)
      .setStrokeStyle(1, 0xc8973a, 0.2).setDepth(201);

    const title = this.add.text(width / 2, height / 2 - panelH / 2 + 14, `Equip: ${item.name}`, {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#c8973a', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(202);

    const elements: Phaser.GameObjects.GameObject[] = [overlay, bg, bgInner, title];

    slots.forEach((slot, i) => {
      const sy = height / 2 - panelH / 2 + 36 + i * 32;
      const current = hero.equipment[slot];
      const slotLocked = current && slot.startsWith('relic')
        && (this.gsm.player.lockedRelics ?? []).includes(current.id);
      const label = current
        ? `${slotNames[slot]}: ${current.name}${slotLocked ? ' [LOCKED]' : ''}`
        : `${slotNames[slot]}: [ Empty ]`;
      const color = slotLocked ? '#5a1818' : (current ? RARITY_COLORS[current.rarity] : '#6b7280');

      const slotBtn = this.add.text(width / 2, sy, label, {
        fontSize: '10px', fontFamily: 'Georgia, serif', color,
        backgroundColor: '#0d0a0e', padding: { x: 12, y: 4 },
      }).setOrigin(0.5).setDepth(202).setInteractive({ useHandCursor: slotLocked ? false : true });
      elements.push(slotBtn);

      if (!slotLocked) {
        slotBtn.on('pointerdown', () => {
          elements.forEach((el) => el.destroy());
          this.gsm.equipItem(this.selectedHeroIndex, item, slot);
          this.refreshContent();
        });
      }
    });

    // Cancel on overlay click
    overlay.on('pointerdown', () => {
      elements.forEach((el) => el.destroy());
    });
  }

  /** Inventory panel when the 'Sig' filter is active — lists only sigils the player has picked
   *  up THIS RUN (the per-save `sigilBag`). Account-collection sigils stay in the main-menu
   *  Whiskers shop and can't be interacted with from here. Fresh loot can still be equipped. */
  private renderSigilInventory(listX: number, itemsY: number, _baseRowH: number): void {
    void _baseRowH; // sigil rows use a taller row height so the affix list fits cleanly.
    // Show only in-run sigils — intentionally NOT merging the account collection.
    const runBag = this.gsm.player.sigilBag ?? [];
    const equipped = this.gsm.player.sigilsEquipped ?? [];
    const slots = this.gsm.effectiveSigilSlots();

    if (runBag.length === 0) {
      const empty = this.add.text(listX + 10, itemsY + 10,
        'No sigils in this run.\nLoot drops show up here. Manage your collection at Rudy (main menu).', {
          fontSize: '10px', fontFamily: 'Georgia, serif', color: '#6b4020', wordWrap: { width: 240 },
        });
      this.contentGroup.add(empty);
      return;
    }

    const ROW_H = 30;
    const ROW_W = 248;
    const ROW_PAGE = 4; // fewer rows so bigger rows fit in the same inventory band
    this.inventoryScrollOffset = Math.min(
      this.inventoryScrollOffset, Math.max(0, runBag.length - ROW_PAGE),
    );
    const page = runBag.slice(this.inventoryScrollOffset, this.inventoryScrollOffset + ROW_PAGE);

    page.forEach((sigil, i) => {
      const y = itemsY + i * ROW_H;
      const eqIdx = equipped.indexOf(sigil.id);
      const isEquipped = eqIdx >= 0;
      const rowCx = listX + ROW_W / 2 - 5; // slight left bias so button column isn't crammed
      const rowCy = y + ROW_H / 2 - 2;

      // Row background + sigil icon
      const rowBg = this.add.rectangle(rowCx, rowCy, ROW_W, ROW_H - 2,
        isEquipped ? 0x1a120a : 0x0d0a08, 0.85,
      ).setStrokeStyle(1, isEquipped ? 0xc0912a : 0x3a2a18).setDepth(1);
      this.contentGroup.add(rowBg);

      if (this.textures.exists(sigil.special.iconKey)) {
        const icon = this.add.image(listX + 12, rowCy, sigil.special.iconKey)
          .setDisplaySize(20, 20).setDepth(2);
        this.contentGroup.add(icon);
      }

      // Row body — name + affixes. The whole row is the click target; no
      // per-slot 1/2/3 buttons here (those moved to InventoryScene's Sig
      // tab). Click the row → equip to first empty slot, or unequip if
      // already equipped. Recycle moved to the Inventory.
      const textX = listX + 26;
      const textMaxWidth = ROW_W - 30;
      const affixStr = sigil.affixes.map(formatAffix).join(', ');
      const nameText = this.add.text(textX, y + 2, sigil.name, {
        fontSize: '10px', fontFamily: 'Georgia, serif', color: isEquipped ? '#f0c040' : '#c8973a', fontStyle: 'bold',
      }).setDepth(2);
      this.contentGroup.add(nameText);
      const affixText = this.add.text(textX, y + 15, affixStr, {
        fontSize: '8px', fontFamily: 'Georgia, serif',
        color: isEquipped ? '#a07840' : '#6b4020',
        wordWrap: { width: textMaxWidth },
      }).setDepth(2);
      this.contentGroup.add(affixText);
      // Status chip — small "EQUIPPED" indicator on the right when slotted,
      // so the row reads at a glance even without explicit per-slot buttons.
      if (isEquipped) {
        const eqLabel = this.add.text(listX + ROW_W - 6, y + ROW_H / 2 - 2, `SLOT ${eqIdx + 1}`, {
          fontSize: '9px', fontFamily: 'Georgia, serif', color: '#f0c040', fontStyle: 'bold',
        }).setOrigin(1, 0.5).setDepth(2);
        this.contentGroup.add(eqLabel);
      }

      // Click anywhere on the row → equip to first empty slot, or unequip
      // if already equipped. Hover shows the tooltip. Right-click reserved
      // for future quick-recycle hook (currently in Inventory).
      rowBg.setInteractive({ useHandCursor: true });
      rowBg.on('pointerdown', () => {
        if (isEquipped) {
          this.gsm.unequipSigil(sigil.id);
        } else if (equipped.length >= slots) {
          this.showMessage('All sigil slots are full — unequip one first.');
          return;
        } else if (!this.gsm.equipSigil(sigil.id)) {
          this.showMessage('Cannot equip.');
          return;
        }
        this.refreshContent();
      });
      rowBg.on('pointerover', () => {
        this.equipTooltip?.destroy();
        this.equipTooltip = createHudTooltip(this, listX + 10, y - 4,
          `${sigil.name}\n${sigil.special.description}\n${affixStr}`,
          0, 1, { fontSize: '10px', wrap: 260, depth: 150 });
      });
      rowBg.on('pointerout', () => { this.equipTooltip?.destroy(); this.equipTooltip = null; });
    });

    if (runBag.length > ROW_PAGE) {
      const scrollInfo = this.add.text(listX, itemsY + ROW_PAGE * ROW_H, `${this.inventoryScrollOffset + 1}-${Math.min(this.inventoryScrollOffset + ROW_PAGE, runBag.length)} / ${runBag.length}`, {
        fontSize: '9px', fontFamily: 'Georgia, serif', color: '#5a3600',
      });
      this.contentGroup.add(scrollInfo);
    }
  }

  /**
   * Small 3-slot strip at the bottom-right showing equipped sigils. Slots beyond the
   * player's effective cap are drawn as locked, with a tooltip naming the passive-tree
   * node required to unlock them (Whisker Bond / Feline Pact).
   */
  private renderSigilStrip(): void {
    // Pyramid layout: 1 slot on top + 2 slots on the bottom row, centered on
    // the avatar at low alpha so the portrait reads through. Lowered so the
    // top slot sits below the resist row in the stats column. User drawing:
    //          [ 0 ]
    //       [ 1 ]  [ 2 ]
    const SIZE = 32;
    const GAP = 6;
    const COUNT = 3;
    const portraitX = 240;
    // Top slot center at y=290 puts it well below the resist row (~y=200)
    // and the bottom row at y=328 lands just above the kills label (y=348).
    const TOP_Y = 290;
    const BOT_Y = TOP_Y + SIZE + GAP;
    const slotPos: Array<{ x: number; y: number }> = [
      { x: portraitX,                  y: TOP_Y }, // index 0 — top center
      { x: portraitX - (SIZE + GAP),   y: BOT_Y }, // index 1 — bottom-left
      { x: portraitX + (SIZE + GAP),   y: BOT_Y }, // index 2 — bottom-right
    ];

    const player = this.gsm.player;
    const equipped = player.sigilsEquipped ?? [];
    const bag = player.sigilBag ?? [];
    const collection = this.gsm.account.sigilCollection ?? [];
    const effSlots = this.gsm.effectiveSigilSlots();

    const DEFAULT_ALPHA = 0.35;
    const HOVER_ALPHA = 1;

    for (let i = 0; i < COUNT; i++) {
      const stripX = slotPos[i].x;
      const cy = slotPos[i].y;
      const unlocked = i < effSlots;
      const sigilId = equipped[i];
      const sigil = sigilId
        ? bag.find((s) => s.id === sigilId) ?? collection.find((s) => s.id === sigilId)
        : undefined;

      // Slot-level container tracks every element so hover can toggle alpha on the whole cell.
      const cellEls: Phaser.GameObjects.GameObject[] = [];
      const bg = this.add.rectangle(stripX, cy, SIZE, SIZE,
        unlocked ? (sigil ? 0x2a1f10 : 0x140e06) : 0x0a0604, 0.92,
      ).setStrokeStyle(1, unlocked ? (sigil ? 0xc0912a : 0x4a3010) : 0x2a1a0a).setDepth(140);
      bg.setAlpha(DEFAULT_ALPHA);
      cellEls.push(bg);
      this.contentGroup.add(bg);

      let tipLines: string[] = [];
      if (sigil) {
        const icon = this.textures.exists(sigil.special.iconKey)
          ? this.add.image(stripX, cy, sigil.special.iconKey).setDisplaySize(SIZE - 6, SIZE - 6).setDepth(141)
          : this.add.text(stripX, cy, '◆', { fontSize: '16px', color: '#f0c040' }).setOrigin(0.5).setDepth(141);
        icon.setAlpha(DEFAULT_ALPHA);
        cellEls.push(icon);
        this.contentGroup.add(icon);
        tipLines = [
          sigil.name,
          sigil.special.description,
          sigil.affixes.map(formatAffix).join(', '),
        ].filter(Boolean);
      } else if (unlocked) {
        const ghost = this.add.text(stripX, cy, '·', {
          fontSize: '18px', fontFamily: 'Georgia, serif', color: '#4a3010',
        }).setOrigin(0.5).setDepth(141);
        ghost.setAlpha(DEFAULT_ALPHA);
        cellEls.push(ghost);
        this.contentGroup.add(ghost);
        tipLines = ['Empty slot', 'Equip a sigil from Rudy (main menu).'];
      } else {
        // Locked: darker background + "Lv N" marker reading requirement stage. No padlock glyph.
        const reqStage = i === 1 ? 20 : 40;
        const reqName = i === 1 ? 'Whisker Bond' : 'Feline Pact';
        const marker = this.add.text(stripX, cy, `S${reqStage}`, {
          fontSize: '9px', fontFamily: 'Georgia, serif', color: '#6b4020', fontStyle: 'bold',
        }).setOrigin(0.5).setDepth(141);
        marker.setAlpha(DEFAULT_ALPHA);
        cellEls.push(marker);
        this.contentGroup.add(marker);
        tipLines = ['Locked sigil slot', `Requires passive "${reqName}" — Stage ${reqStage}.`];
      }

      bg.setInteractive({ useHandCursor: true });
      bg.on('pointerover', () => {
        cellEls.forEach((el) => (el as Phaser.GameObjects.Sprite & { setAlpha: (v: number) => unknown }).setAlpha(HOVER_ALPHA));
        this.equipTooltip?.destroy();
        // Anchor the tooltip ABOVE the sigil cell (origin 0.5, 1) so it doesn't
        // run off either edge of the panel for the leftmost or rightmost cell.
        const tipY = cy - SIZE / 2 - 6;
        this.equipTooltip = createHudTooltip(this, stripX, tipY, tipLines.join('\n'),
          0.5, 1, { fontSize: '10px', wrap: 220, depth: 155 });
      });
      bg.on('pointerout', () => {
        cellEls.forEach((el) => (el as Phaser.GameObjects.Sprite & { setAlpha: (v: number) => unknown }).setAlpha(DEFAULT_ALPHA));
        this.equipTooltip?.destroy();
        this.equipTooltip = null;
      });
      // Double-click on an equipped cell unequips the sigil — fast unequip without
      // having to find the corresponding ✓ button in the inventory list below.
      if (sigil) {
        let lastClickAt = 0;
        bg.on('pointerdown', () => {
          const now = Date.now();
          if (now - lastClickAt < 400) {
            this.gsm.unequipSigil(sigil.id);
            this.refreshContent();
            lastClickAt = 0;
          } else {
            lastClickAt = now;
          }
        });
      }
    }
  }

  private addStatLine(x: number, y: number, label: string, value: string, color: string): void {
    const l = this.add.text(x, y, `${label}:`, { fontSize: '10px', fontFamily: 'Georgia, serif', color: '#6b4020', fontStyle: 'bold' }).setResolution(2);
    const v = this.add.text(x + 90, y, value, { fontSize: '10px', fontFamily: 'Georgia, serif', color, fontStyle: 'bold' }).setResolution(2);
    this.contentGroup.add(l);
    this.contentGroup.add(v);
  }

  private addSectionHeader(x: number, y: number, text: string): void {
    const t = this.add.text(x, y, `✦ ${text}`, {
      fontSize: '10px', fontFamily: 'Georgia, serif', color: '#c8973a', fontStyle: 'bold',
    });
    this.contentGroup.add(t);
  }

  private showMessage(text: string): void {
    const msg = this.add.text(240, 540, text, {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#c8973a',
      backgroundColor: '#0d0a0e', padding: { x: 10, y: 5 },
      stroke: '#5a3600', strokeThickness: 1,
    }).setOrigin(0.5).setDepth(100);
    this.tweens.add({ targets: msg, alpha: 0, delay: 2000, duration: 500, onComplete: () => msg.destroy() });
  }

  private closeScene(): void {
    this.searchInput?.destroy();
    this.searchInput = null;
    this.input.keyboard?.enableGlobalCapture();
    this.gsm.save();
    this.battleScene.resumeBattle();
    this.scene.stop();
  }
}
